import { useState } from "react";
import {
  useDateFilters,
  useDebouncedTableFilter,
  useRange,
} from "shared/hooks";

export const useTaskHubFilters = () => {
  const {
    debouncedValue: debouncedTaskNameValue,
    handleFilterChange: handleTaskNameValue,
    value: taskNameValue,
    clearFilterValue: clearTaskNameValue,
  } = useDebouncedTableFilter("taskName", 400);

  const {
    debouncedValue: debouncedPeriodicityValue,
    handleFilterChange: handlePeriodicityValue,
    value: periodicityValue,
    clearFilterValue: clearPeriodicityValue,
  } = useDebouncedTableFilter("periodicity", 400);

  const {
    debouncedValue: debouncedStandAloneNameValue,
    handleFilterChange: handleStandAloneNameValue,
    clearFilterValue: clearStandAloneNameValue,
    value: standAloneNameValue,
  } = useDebouncedTableFilter("standAloneName", 400);

  const {
    debouncedValue: debouncedCommentsValue,
    handleFilterChange: handleCommentsValue,
    value: commentsValue,
    clearFilterValue: clearCommentsValue,
  } = useDebouncedTableFilter("comments", 400);

  const {
    debouncedValue: debouncedMeasurementValue,
    value: measurementValue,
    handleFilterChange: handleMeasurementValue,
    clearFilterValue: clearMeasurementValue,
  } = useDebouncedTableFilter("measurement", 400);

  const {
    debouncedValue: debouncedFieldValue,
    value: fieldValue,
    handleFilterChange: handleFieldValue,
    clearFilterValue: clearFieldValue,
  } = useDebouncedTableFilter("field", 400);

  const lookbackRange = useRange({ maxValueLimit: 999999 });
  const priorityRange = useRange({ maxValueLimit: 999999 });
  const targetRange = useRange({ maxValueLimit: 999999 });
  const minSoftRange = useRange({ maxValueLimit: 999999 });
  const maxSoftRange = useRange({ maxValueLimit: 999999 });
  const minHardRange = useRange({ maxValueLimit: 999999 });
  const maxHardRange = useRange({ maxValueLimit: 999999 });
  const priorityFilters = useRange({ maxValueLimit: 999999 });

  const lastTimestampFilters = useDateFilters();
  const timeFilters = useDateFilters();
  const startDateFilters = useDateFilters();
  const endDateFilters = useDateFilters();
  const createdFilters = useDateFilters();
  const updatedFilters = useDateFilters();
  const triggeredFilters = useDateFilters();

  const [enabledFilter, setEnabledFilter] = useState<boolean | undefined>();
  const [userIds, setUserIds] = useState<number[]>([]);
  const [serverFilter, setServerFilter] = useState<number | undefined>();
  const [taskStandaloneFilter, setTaskStandaloneFilter] = useState<number[]>(
    []
  );
  const [isActiveFilter, setIsActiveFilter] = useState<boolean | undefined>(
    true
  );

  const [isMinSoftFilter, setIsMinSoftFilter] = useState<boolean | undefined>();
  const [isMaxSoftFilter, setIsMaxSoftFilter] = useState<boolean | undefined>();
  const [isMinHardFilter, setIsMinHardFilter] = useState<boolean | undefined>();
  const [isMaxHardFilter, setIsMaxHardFilter] = useState<boolean | undefined>();

  const [maxValueGte, setMaxValueGte] = useState<number | undefined>();
  const [maxValueLte, setMaxValueLte] = useState<number | undefined>();
  const [minValueGte, setMinValueGte] = useState<number | undefined>();
  const [minValueLte, setMinValueLte] = useState<number | undefined>();

  const clearAllFilters = () => {
    clearTaskNameValue();
    clearPeriodicityValue();
    clearStandAloneNameValue();
    clearCommentsValue();
    clearMeasurementValue();
    clearFieldValue();

    setEnabledFilter(undefined);
    setUserIds([]);
    setServerFilter(undefined);
    setTaskStandaloneFilter([]);
    setIsActiveFilter(true);

    setIsMinSoftFilter(undefined);
    setIsMaxSoftFilter(undefined);
    setIsMinHardFilter(undefined);
    setIsMaxHardFilter(undefined);

    setMaxValueGte(undefined);
    setMaxValueLte(undefined);
    setMinValueGte(undefined);
    setMinValueLte(undefined);

    lookbackRange.reset(0, 999999);
    priorityRange.reset(0, 999999);
    targetRange.reset(0, 999999);
    priorityFilters.reset(0, 999999);
    minSoftRange.reset(0, 999999);
    maxSoftRange.reset(0, 999999);
    minHardRange.reset(0, 999999);
    maxHardRange.reset(0, 999999);

    [
      timeFilters,
      startDateFilters,
      endDateFilters,
      createdFilters,
      updatedFilters,
      triggeredFilters,
      lastTimestampFilters,
    ].forEach((f) => f.clearDates());
  };

  const isAnyDateFilterActive = (
    ...filters: ReturnType<typeof useDateFilters>[]
  ) => filters.some((f) => f.dateGreater || f.dateLesser);

  const isVisibleClearFiltersButton =
    !!taskNameValue ||
    !!periodicityValue ||
    !!standAloneNameValue ||
    !!commentsValue ||
    !!measurementValue ||
    !!fieldValue ||
    enabledFilter !== undefined ||
    userIds.length > 0 ||
    !!serverFilter ||
    taskStandaloneFilter.length > 0 ||
    !isActiveFilter ||
    lookbackRange.isFilterActive() ||
    priorityRange.isFilterActive() ||
    targetRange.isFilterActive() ||
    priorityFilters.isFilterActive() ||
    minSoftRange.isFilterActive() ||
    maxSoftRange.isFilterActive() ||
    minHardRange.isFilterActive() ||
    maxHardRange.isFilterActive() ||
    isAnyDateFilterActive(
      lastTimestampFilters,
      timeFilters,
      startDateFilters,
      endDateFilters,
      createdFilters,
      updatedFilters,
      triggeredFilters
    );

  return {
    taskNameValue,
    periodicityValue,
    standAloneNameValue,
    commentsValue,
    measurementValue,
    fieldValue,

    debouncedTaskNameValue,
    debouncedPeriodicityValue,
    debouncedStandAloneNameValue,
    debouncedCommentsValue,
    debouncedMeasurementValue,
    debouncedFieldValue,

    lookbackRange,
    priorityRange,
    targetRange,
    priorityFilters,
    minSoftRange,
    maxSoftRange,
    minHardRange,
    maxHardRange,

    lastTimestampFilters,
    timeFilters,
    startDateFilters,
    endDateFilters,
    createdFilters,
    updatedFilters,
    triggeredFilters,

    enabledFilter,
    setEnabledFilter,
    userIds,
    setUserIds,
    serverFilter,
    setServerFilter,
    taskStandaloneFilter,
    setTaskStandaloneFilter,
    isActiveFilter,
    setIsActiveFilter,
    isMinSoftFilter,
    setIsMinSoftFilter,
    isMaxSoftFilter,
    setIsMaxSoftFilter,
    isMinHardFilter,
    setIsMinHardFilter,
    isMaxHardFilter,
    setIsMaxHardFilter,
    maxValueGte,
    setMaxValueGte,
    maxValueLte,
    setMaxValueLte,
    minValueGte,
    setMinValueGte,
    minValueLte,
    setMinValueLte,

    handleTaskNameValue,
    handlePeriodicityValue,
    handleStandAloneNameValue,
    handleCommentsValue,
    handleMeasurementValue,
    handleFieldValue,

    clearTaskNameValue,
    clearPeriodicityValue,
    clearStandAloneNameValue,
    clearCommentsValue,
    clearMeasurementValue,
    clearFieldValue,

    clearAllFilters,
    isVisibleClearFiltersButton,
  };
};
